import cx from 'classnames'
import { forwardRef, useState } from 'react'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { useDocs } from 'common/DocPicker/useDocs'
import { useRecentArticles } from 'hooks/queries/articles/useRecentArticles'
import { useFavorites } from 'hooks/queries/favorites/useFavorites'
import Styles from './FromDoc.module.scss'
import { useMountEffect } from '@react-hookz/web'
import { ComboBox } from 'components/ComboBox/ComboBox'
import { Item, Section } from '@react-stately/collections'
import { collectResults } from 'utils/query'
import { FavoritableType } from 'api/clients/FavoriteClient'
import { Document } from 'almanac-ui/icons'
import { ArticleClient } from 'api/clients/ArticleClient'
import { SourceCard } from 'components/SourceCard/SourceCard'
import { useObjectRef } from '@react-aria/utils'

export type FromDocProps = {
  fullWidth?: boolean
  onChange: (sampleText: string, doc: Doc | undefined) => void
  selectedDoc?: Doc
  fetchImmediately?: boolean
}

export type Doc = { id: string; name: string; emoji: string | null }

interface SuggestionsSection {
  name: string
  children: Doc[]
}

export const FromDoc = forwardRef<HTMLInputElement, FromDocProps>(
  ({ onChange, fullWidth, selectedDoc, fetchImmediately = true }, fRef) => {
    const [query, setQuery] = useState('')
    const inputRef = useObjectRef<HTMLInputElement>(fRef)
    const { data: favorites } = useFavorites()

    useMountEffect(() => {
      if (selectedDoc) {
        handleSelectDoc(selectedDoc.id)
      }
    })

    const favoriteFiles = favorites
      ?.filter((f) => f.favoritable_type === FavoritableType.Article)
      .map((f) => ({
        id: f.favoritable_id,
        name: f.favoritable_title,
        emoji: f.favoritable_emoji,
      }))
    const docs = useDocs(query, query !== '')
    const recentFiles = collectResults(useRecentArticles({ query: { items: 10 } }), (data) => data.articles)
      .filter((f) => f.type === 'article' && !favoriteFiles?.find((favorite) => favorite.id === f.id))
      .map((file) => ({ id: file.id, name: file.title, emoji: null }))
    const searchResults = docs?.map((doc) => ({ id: doc.key, name: doc.title, emoji: doc.header_emoji }))

    const results: SuggestionsSection[] = []
    if (query === '') {
      if (favoriteFiles && favoriteFiles.length > 0) {
        results.push({ name: 'Favorites', children: favoriteFiles })
      }
      if (recentFiles.length > 0) {
        results.push({ name: 'Recents', children: recentFiles })
      }
    } else {
      if (searchResults && searchResults.length > 0) {
        results.push({ name: 'Search Results', children: searchResults })
      }
    }

    const allResults = results.flatMap((r) => r.children)

    const handleSelectDoc = async (id: string) => {
      const doc = allResults.find((r) => r.id === id)
      setQuery('')
      onChange('', doc)
      if (!fetchImmediately) return

      ArticleClient.getArticleAsMarkdown(id).then((response) => {
        if (response.type !== SUCCESS_RESPONSE_TYPE) {
          throw new Error(`Failed to extract doc text: ${response}`)
        }
        onChange(response.data.markdown, doc)
      })
    }

    return (
      <div className={cx(Styles.FromDoc, { [Styles.fullWidth]: fullWidth })}>
        {selectedDoc ? (
          <SourceCard
            icon={selectedDoc.emoji ?? <Document />}
            title={selectedDoc.name}
            onDismiss={() => {
              setQuery('')
              onChange('', undefined)
              inputRef.current?.focus()
            }}
          />
        ) : (
          <ComboBox
            onOpenChange={(isOpen) => {
              if (!isOpen) {
                inputRef.current?.blur()
              }
            }}
            ref={inputRef}
            menuTrigger="focus"
            size="lg"
            noResultsMessage={<div className={Styles.emptyState}>No results</div>}
            onInputChange={setQuery}
            inputValue={query}
            onSelectionChange={(key) => handleSelectDoc(key as string)}
            selectedKey={selectedDoc}
            aria-label="Document name"
            items={results}
            popoverClassName={Styles.popover}
            placeholder="Search..."
            optionClassName={Styles.option}
          >
            {(item: SuggestionsSection) => (
              <Section key={item.name} items={item.children} title={item.name}>
                {(item) => (
                  <Item textValue={item.name}>
                    {item.emoji || <Document />}
                    <span className={Styles.title}>{item.name}</span>
                  </Item>
                )}
              </Section>
            )}
          </ComboBox>
        )}
      </div>
    )
  },
)
